import { evaluateAdsensePageSafety, readAdsenseSafetyState } from './adsenseSafetyGate.js';
import { loadGooglePublisherSdk } from './googlePublisherSdk.js';
import { reportRuntimeEvent } from './runtimeTelemetry.js';
import { createDisplayRefresh } from './displayRefresh.js';

let nextSlotId = 0;
const SAFETY_EVENT = 'watchvim:google-ad-safety';

// Timed refresh stays off until the matching GAM inventory declaration is verified.
export function mountGamDisplay({ host, adUnitPath, refreshDeclared = false, onStatus }) {
  const element = document.createElement('div');
  element.id = `watchvim-gam-rectangle-${++nextSlotId}`;
  element.style.width = '300px';
  element.style.height = '250px';
  element.dataset.adUnitPath = adUnitPath;
  host.replaceChildren(element);
  let disposed = false;
  let finished = false;
  let queued = false;
  let requested = false;
  let slot = null;
  let service = null;
  let timer = 0;
  let requestedNpa = null;
  let refreshInterval = 0;
  let inView = false;
  let observer = null;
  const refreshEligible = () => {
    const state = readAdsenseSafetyState();
    const player = document.querySelector('.playback-page .watchvim-content-player');
    const fullscreen = document.fullscreenElement;
    const mobileFullscreen = document.querySelector('.playback-stage.is-watchvim-mobile-fullscreen');
    return !disposed && !finished && element.isConnected && inView
      && document.visibilityState === 'visible'
      && !mobileFullscreen && player?.media?.nativeEl?.webkitDisplayingFullscreen !== true
      && (!fullscreen || fullscreen.contains(element))
      && player?.paused === false && player?.ended !== true
      && state.consentEligible && state.pageEligible && currentPageEligible();
  };
  const refreshCycle = createDisplayRefresh({
    eligible: refreshEligible,
    refresh: () => {
      try {
        requestedNpa = readAdsenseSafetyState().requestNonPersonalizedAds;
        service.setPrivacySettings({ nonPersonalizedAds: requestedNpa });
        diagnostic('refresh');
        service.refresh([slot]);
      } catch (_) { finish('error', 'WATCHVIM_GPT_REQUEST_FAILED'); }
    },
  });
  const clearTimer = () => { window.clearTimeout(timer); timer = 0; };
  const destroySlot = () => {
    clearTimer();
    refreshCycle.stop();
    window.clearInterval(refreshInterval);
    observer?.disconnect();
    if (service) {
      service.removeEventListener('slotRenderEnded', rendered);
      service.removeEventListener('slotRequested', requestStarted);
    }
    if (slot) window.googletag.destroySlots([slot]);
    slot = null;
  };
  const diagnostic = (event, code = '') => {
    host.dataset.googleDisplayEvent = event;
    host.dataset.googleDisplayCode = code;
    reportRuntimeEvent({ event, code, tagSource: 'google-display', position: 'display' });
  };
  const finish = (status, code = '') => {
    if (disposed || finished) return;
    finished = true;
    diagnostic(status, code);
    destroySlot();
    onStatus(status);
  };
  const rendered = (event) => {
    if (disposed || finished || event.slot !== slot) return;
    clearTimer();
    element.dataset.fill = event.isEmpty ? 'unfilled' : 'filled';
    if (event.isEmpty) finish('unfilled');
    else { diagnostic('filled'); onStatus('gam'); refreshCycle.rendered(); }
  };
  const requestStarted = (event) => {
    if (disposed || finished || event.slot !== slot) return;
    clearTimer();
    diagnostic('requested');
    timer = window.setTimeout(() => finish('error', 'WATCHVIM_GPT_RENDER_TIMEOUT'), 15000);
  };
  const currentPageEligible = () => evaluateAdsensePageSafety({ pathname: window.location.pathname }).eligible;
  const evaluate = () => {
    if (disposed || finished || !element.isConnected) return;
    if (!refreshEligible()) refreshCycle.reset();
    const state = readAdsenseSafetyState();
    // Remove an already requested creative when permission is revoked or narrowed.
    if (requested && (!state.consentEligible || (requestedNpa === false && state.requestNonPersonalizedAds))) {
      finish('unfilled');
      return;
    }
    if (!state.consentEligible || !state.pageEligible || !currentPageEligible()) return;
    if (requested || queued) return;
    queued = true;
    diagnostic('loading');
    loadGooglePublisherSdk().then(() => {
      queued = false;
      if (disposed || finished || !element.isConnected) return;
      const current = readAdsenseSafetyState();
      if (!current.consentEligible || !current.pageEligible || !currentPageEligible()) {
        clearTimer();
        return;
      }
      try {
        const gpt = window.googletag;
        gpt.setConfig({ disableInitialLoad: true });
        service = gpt.pubads();
        requestedNpa = current.requestNonPersonalizedAds;
        service.setPrivacySettings({ nonPersonalizedAds: requestedNpa });
        slot = gpt.defineSlot(adUnitPath, [300, 250], element.id);
        if (!slot) { finish('error'); return; }
        slot.addService(service);
        service.addEventListener('slotRenderEnded', rendered);
        service.addEventListener('slotRequested', requestStarted);
        gpt.enableServices();
        gpt.display(element.id);
        requested = true;
        element.dataset.requested = 'true';
        // Initial request remains separate from the declared refresh cycle.
        service.refresh([slot]);
      } catch (_) { finish('error', 'WATCHVIM_GPT_REQUEST_FAILED'); }
    }).catch(() => finish('error', 'WATCHVIM_GPT_LOAD_FAILED'));
  };
  window.addEventListener(SAFETY_EVENT, evaluate);
  document.addEventListener('visibilitychange', evaluate);
  document.addEventListener('fullscreenchange', evaluate);
  document.addEventListener('pause', evaluate, true);
  if (refreshDeclared === true && typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(([entry]) => {
      inView = entry?.isIntersecting === true && entry.intersectionRatio >= 0.5;
      if (!inView) refreshCycle.reset();
    }, { threshold: [0, 0.5, 1] });
    observer.observe(element);
    refreshInterval = window.setInterval(() => refreshCycle.tick(), 1000);
  }
  evaluate();
  return () => {
    disposed = true;
    window.removeEventListener(SAFETY_EVENT, evaluate);
    document.removeEventListener('visibilitychange', evaluate);
    document.removeEventListener('fullscreenchange', evaluate);
    document.removeEventListener('pause', evaluate, true);
    destroySlot();
    if (host.contains(element)) host.replaceChildren();
  };
}
